import { all, call, put, select, takeLatest } from "typed-redux-saga";
import { alertControls, loaderControls } from "react-better-html";

import { type RootState } from "../store";
import { workspacesSlice } from "../slices/workspaces";

import { api } from "../../utils/api";
import { handleError } from "../../utils/functions";
import { LocalStorage } from "../../utils/localStorage";
import { i18n } from "../../utils/i18n";

function* onGetAllWorkspaces(action: ReturnType<typeof workspacesSlice.actions.getAllWorkspaces>) {
   const selectedOrganizationId = yield* select((state: RootState) => state.organizations.selectedOrganizationId);

   if (!selectedOrganizationId) return;

   yield* call(() => loaderControls.startLoading("workspaces:getAllWorkspaces"));

   try {
      const response = yield* call(() =>
         api("getAllWorkspaces", {
            path: [selectedOrganizationId, "workspaces"],
         }),
      );

      yield* put(workspacesSlice.actions.setWorkspaces(response.data.active));
      yield* put(workspacesSlice.actions.setArchivedWorkspaces(response.data.archived));

      action.payload?.onSuccess?.(response.data);
   } catch (error) {
      action.payload?.onError?.(error);
      yield* call(handleError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("workspaces:getAllWorkspaces"));
   }
}

function* onGetWorkspaceById(action: ReturnType<typeof workspacesSlice.actions.getWorkspaceById>) {
   yield* call(() => loaderControls.startLoading("workspaces:getWorkspaceById"));
   yield* call(() => loaderControls.startLoading(`workspaces:getWorkspaceById${action.payload.id}`));

   try {
      const response = yield* call(() =>
         api("getWorkspaceById", {
            path: [action.payload.id],
         }),
      );

      yield* put(workspacesSlice.actions.updateWorkspace(response.data));

      action.payload.onSuccess?.(response.data);
   } catch (error) {
      action.payload.onError?.(error);
      yield* call(handleError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("workspaces:getWorkspaceById"));
      yield* call(() => loaderControls.stopLoading(`workspaces:getWorkspaceById${action.payload.id}`));
   }
}

function* onSetWorkspaces(action: ReturnType<typeof workspacesSlice.actions.setWorkspaces>) {
   yield* call(() =>
      action.payload ? LocalStorage.setItem("workspaces", action.payload) : LocalStorage.removeItem("workspaces"),
   );
}

function* onCreateWorkspace(action: ReturnType<typeof workspacesSlice.actions.createWorkspace>) {
   const selectedOrganizationId = yield* select((state: RootState) => state.organizations.selectedOrganizationId);

   if (!selectedOrganizationId) return;

   yield* call(() => loaderControls.startLoading("workspaces:createWorkspace"));

   try {
      const response = yield* call(() =>
         api("createWorkspace", {
            body: action.payload.data,
            path: [selectedOrganizationId, "workspaces"],
         }),
      );

      yield* put(workspacesSlice.actions.updateWorkspace(response.data));

      yield* call(() =>
         alertControls.createAlert({
            type: "success",
            title: i18n.t((l) => l.api.success.default),
            message: i18n.t((l) => l.api.success.list.workspaceCreated),
         }),
      );

      action.payload.onSuccess?.(response.data);
   } catch (error) {
      action.payload.onError?.(error);
      yield* call(handleError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("workspaces:createWorkspace"));
   }
}

function* onEditWorkspace(action: ReturnType<typeof workspacesSlice.actions.editWorkspace>) {
   yield* call(() => loaderControls.startLoading("workspaces:editWorkspace"));
   yield* call(() => loaderControls.startLoading(`workspaces:editWorkspace${action.payload.id}`));

   try {
      const response = yield* call(() =>
         api("editWorkspace", {
            body: action.payload.data,
            path: [action.payload.id],
         }),
      );

      yield* put(workspacesSlice.actions.updateWorkspace(response.data));

      yield* call(() =>
         alertControls.createAlert({
            type: "success",
            title: i18n.t((l) => l.api.success.default),
            message: i18n.t((l) => l.api.success.list.workspaceEdited),
         }),
      );

      action.payload.onSuccess?.(response.data);
   } catch (error) {
      action.payload.onError?.(error);
      yield* call(handleError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("workspaces:editWorkspace"));
      yield* call(() => loaderControls.stopLoading(`workspaces:editWorkspace${action.payload.id}`));
   }
}

function* onUpdateWorkspace() {
   const workspacesList = yield* select((state: RootState) => state.workspaces.list);

   yield* call(() =>
      workspacesList ? LocalStorage.setItem("workspaces", workspacesList) : LocalStorage.removeItem("workspaces"),
   );
}

function* onWorkspaceOperations(action: ReturnType<typeof workspacesSlice.actions.workspaceOperations>) {
   const selectedOrganizationId = yield* select((state: RootState) => state.organizations.selectedOrganizationId);

   if (!selectedOrganizationId) return;

   yield* call(() => loaderControls.startLoading("workspaces:workspaceOperations"));
   yield* call(() => loaderControls.startLoading(`workspaces:workspaceOperations${action.payload.id}`));

   try {
      const response = yield* call(() =>
         api("workspaceOperations", {
            body: action.payload.data,
            path: [action.payload.id, "operations"],
         }),
      );

      yield* put(workspacesSlice.actions.updateWorkspace(response.data));

      if (action.payload.data.operation === "ARCHIVE") {
         yield* call(() =>
            alertControls.createAlert({
               type: "success",
               title: i18n.t((l) => l.api.success.default),
               message: i18n.t((l) => l.api.success.list.workspaceArchived),
            }),
         );
      } else if (action.payload.data.operation === "UNARCHIVE") {
         yield* call(() =>
            alertControls.createAlert({
               type: "success",
               title: i18n.t((l) => l.api.success.default),
               message: i18n.t((l) => l.api.success.list.workspaceUnarchived),
            }),
         );
      }

      action.payload.onSuccess?.(response.data);
   } catch (error) {
      action.payload.onError?.(error);
      yield* call(handleError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("workspaces:workspaceOperations"));
      yield* call(() => loaderControls.stopLoading(`workspaces:workspaceOperations${action.payload.id}`));
   }
}

function* onCreateWorkspaceShareLink(action: ReturnType<typeof workspacesSlice.actions.createWorkspaceShareLink>) {
   yield* call(() => loaderControls.startLoading("workspaces:createWorkspaceShareLink"));

   try {
      const response = yield* call(() =>
         api("createWorkspaceShareLink", {
            path: [action.payload.id, "share-link"],
         }),
      );

      // yield* put(workspacesSlice.actions.updateWorkspace(response.data));

      yield* call(() =>
         alertControls.createAlert({
            type: "success",
            title: i18n.t((l) => l.api.success.default),
            message: i18n.t((l) => l.api.success.list.workspaceShareLinkCreated),
         }),
      );

      action.payload.onSuccess?.(response.data);
   } catch (error) {
      action.payload.onError?.(error);
      yield* call(handleError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("workspaces:createWorkspaceShareLink"));
   }
}

export default function* workspacesSaga() {
   yield* all([
      takeLatest(workspacesSlice.actions.getAllWorkspaces, onGetAllWorkspaces),
      takeLatest(workspacesSlice.actions.getWorkspaceById, onGetWorkspaceById),
      takeLatest(workspacesSlice.actions.setWorkspaces, onSetWorkspaces),
      takeLatest(workspacesSlice.actions.createWorkspace, onCreateWorkspace),
      takeLatest(workspacesSlice.actions.editWorkspace, onEditWorkspace),
      takeLatest(workspacesSlice.actions.updateWorkspace, onUpdateWorkspace),
      takeLatest(workspacesSlice.actions.workspaceOperations, onWorkspaceOperations),
      takeLatest(workspacesSlice.actions.createWorkspaceShareLink, onCreateWorkspaceShareLink),
   ]);
}
